import {
  CONTENT_I18N_ROW_STATE_KEY,
  deriveKbArticleTableOfContents,
  externalVideoEmbedUrl,
  externalVideoWatchUrl,
  isKbArticleBlockEnabled,
  KB_ARTICLE_BLOCK_DOCUMENT_VERSION,
  type KbArticleBlock,
  type KbArticleBlockDocument,
  type KbArticleBlockSourceRow,
  parseContentI18nRowState,
  resolveKbArticleDocumentFromBlockRows as resolveKbArticleDocumentFromSourceRows,
  safeParseKbArticleBlockDocument,
} from '@streamlain/view-config';
import {
  createElement,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  BoundDataRow,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { ResponsiveImage } from '../../responsive-image/responsive-image';
import { resolveCustomAppRuntimeHref } from '../runtime-href';
import * as styles from '../styles.css';
import * as markdownStyles from './markdown-renderer.css';
import { renderSanitizedMarkdown } from './markdown-sanitizer';

export function hasKbArticleBlocks(row: BoundDataRow): boolean {
  const rawValue = row['blocks'];
  if (typeof rawValue === 'string' && rawValue.trim() === '') return false;
  const document = safeParseKbArticleBlockDocument(rawValue);
  return (
    document !== null &&
    document.blocks.some(block => isKbArticleBlockEnabled(block))
  );
}

export function hasKbArticleDocument(
  row: BoundDataRow,
  runtimeContext?: ViewRuntimeContext
): boolean {
  const inlineDocument = resolveInlineKbArticleDocument(row);
  if (inlineDocument !== undefined) return inlineDocument !== null;
  const fromRows = resolveKbArticleDocumentFromBlockRows(runtimeContext);
  return fromRows.matchedRowCount > 0 || fromRows.document !== null;
}

export function KbArticleRenderer({
  row,
  runtimeContext,
  workspaceId,
}: {
  row: BoundDataRow;
  runtimeContext?: ViewRuntimeContext;
  workspaceId?: string;
}): ReactElement {
  const document = useMemo(
    () => resolveKbArticleDocument(row, runtimeContext),
    [row, runtimeContext]
  );
  const articleRef = useRef<HTMLElement>(null);
  const toc = useMemo(
    () => (document ? deriveKbArticleTableOfContents(document) : []),
    [document]
  );
  const { activeItemId, navigateToItem } = useArticleTableOfContents(
    articleRef,
    toc
  );
  const progress = useReadingProgress();

  if (!document) {
    return createElement(
      'article',
      {
        ref: articleRef,
        className: styles.kbArticleShell,
        'data-sl-role': 'kb-article',
      },
      createElement(
        'div',
        { className: styles.kbArticleInvalid },
        'Article block data is invalid.'
      )
    );
  }

  const title = readString(row, ['title', 'name']) ?? 'Untitled';
  const description = readString(row, ['description', 'summary']);
  const readTime = readString(row, ['readTime', 'readingTime']);
  const publishedAt = formatDate(readString(row, ['publishedAt', 'date']));
  const author = resolveAuthorName(row, runtimeContext);
  const image = readCell(row, ['image', 'cover', 'heroImage']);
  const imageAlt = readString(row, ['imageAlt', 'alt', 'coverAlt']) ?? title;
  const i18nState = parseContentI18nRowState(row[CONTENT_I18N_ROW_STATE_KEY]);
  return createElement(
    'article',
    {
      ref: articleRef,
      className: styles.kbArticleShell,
      'data-sl-role': 'kb-article',
    },
    createElement('div', {
      className: styles.kbReadingProgress,
      style: { transform: `scaleX(${progress})` },
      'aria-hidden': 'true',
      'data-sl-role': 'kb-reading-progress',
    }),
    i18nState?.state === 'fallback'
      ? createElement(
          'aside',
          {
            className: styles.kbArticleLanguageFallback,
            'data-sl-role': 'kb-language-fallback',
          },
          fallbackNotice(i18nState.requestedLocale, i18nState.defaultLocale)
        )
      : null,
    createElement(
      'header',
      {
        className: styles.kbArticleHeader,
        'data-sl-role': 'kb-article-header',
      },
      createElement(
        'div',
        {
          className: styles.kbArticleMeta,
          'data-sl-field': 'meta',
          'data-sl-role': 'kb-article-meta',
        },
        [author, readTime, publishedAt].filter(Boolean).join(' / ')
      ),
      createElement(
        'h1',
        { className: styles.kbArticleTitle, 'data-sl-field': 'title' },
        title
      ),
      description
        ? createElement(
            'p',
            {
              className: styles.kbArticleDescription,
              'data-sl-field': 'description',
            },
            description
          )
        : null,
      image
        ? createElement(
            'div',
            {
              className: styles.kbArticleHero,
              'data-sl-role': 'kb-article-hero',
            },
            createElement(ResponsiveImage, {
              image,
              role: 'hero',
              alt: imageAlt,
              workspaceId,
              allowWorkspaceBlobFallback: Boolean(workspaceId),
              className: styles.kbArticleHeroImage,
            })
          )
        : null
    ),
    createElement(
      'div',
      {
        className:
          toc.length > 1
            ? `${styles.kbArticleLayout} ${styles.kbArticleLayoutWithToc}`
            : styles.kbArticleLayout,
        'data-sl-role': 'kb-article-layout',
        'data-has-toc': toc.length > 1 ? 'true' : 'false',
      },
      toc.length > 1
        ? renderTableOfContents(toc, activeItemId, navigateToItem)
        : null,
      createElement(
        'div',
        { className: styles.kbArticleMain, 'data-sl-role': 'kb-article-main' },
        ...document.blocks
          .filter(isKbArticleBlockEnabled)
          .map(block =>
            renderKbArticleBlock(block, workspaceId, runtimeContext)
          )
      )
    )
  );
}

function fallbackNotice(
  requestedLocale: string,
  defaultLocale: string
): string {
  if (requestedLocale.toLowerCase().startsWith('de')) {
    const adjective = defaultLocale.toLowerCase().startsWith('en')
      ? 'englische'
      : defaultLocale.toLowerCase().startsWith('de')
        ? 'deutsche'
        : `${languageName(defaultLocale, requestedLocale)}sprachige`;
    return `Dieser Artikel ist auf Deutsch nicht verfügbar. Die ${adjective} Version wird angezeigt.`;
  }
  return `This article is not available in ${languageName(requestedLocale, requestedLocale)}. The ${languageName(defaultLocale, requestedLocale)} version is shown.`;
}

function languageName(locale: string, displayLocale: string): string {
  try {
    return (
      new Intl.DisplayNames([displayLocale], { type: 'language' }).of(locale) ??
      locale.toUpperCase()
    );
  } catch {
    return locale.toUpperCase();
  }
}

function resolveKbArticleDocument(
  row: BoundDataRow,
  runtimeContext?: ViewRuntimeContext
): KbArticleBlockDocument | null {
  const inlineDocument = resolveInlineKbArticleDocument(row);
  if (inlineDocument !== undefined) return inlineDocument;
  const fromRows = resolveKbArticleDocumentFromBlockRows(runtimeContext);
  if (fromRows.matchedRowCount > 0) return fromRows.document;
  return null;
}

function resolveInlineKbArticleDocument(
  row: BoundDataRow
): KbArticleBlockDocument | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(row, 'blocks')) return undefined;
  const rawValue = row['blocks'];
  if (
    rawValue == null ||
    (typeof rawValue === 'string' && rawValue.trim() === '')
  ) {
    return { version: KB_ARTICLE_BLOCK_DOCUMENT_VERSION, blocks: [] };
  }
  return safeParseKbArticleBlockDocument(rawValue);
}

function resolveKbArticleDocumentFromBlockRows(
  runtimeContext?: ViewRuntimeContext
): ReturnType<typeof resolveKbArticleDocumentFromSourceRows> {
  const rows = resolveArticleBlockRows(runtimeContext);
  const articleRowId =
    typeof runtimeContext?.row?.id === 'string' ? runtimeContext.row.id : '';
  return resolveKbArticleDocumentFromSourceRows({
    articleRowId,
    articleSlug: runtimeContext?.routeParams?.['slug'],
    rows,
  });
}

function resolveArticleBlockRows(
  runtimeContext?: ViewRuntimeContext
): KbArticleBlockSourceRow[] {
  const articleRowId =
    typeof runtimeContext?.row?.id === 'string' ? runtimeContext.row.id : null;
  if (!articleRowId) return [];
  const source = runtimeContext?.sources?.['articleBlocks'];
  const rows = readBoundRowsFromRuntimeSource(source);
  return rows
    .filter(row => {
      const relation = row['article'];
      if (Array.isArray(relation)) return relation.includes(articleRowId);
      return (
        relation === articleRowId ||
        row['articleId'] === articleRowId ||
        row['articleId'] === runtimeContext?.routeParams?.['slug']
      );
    })
    .map(
      (row, index): KbArticleBlockSourceRow => ({
        ...row,
        id:
          typeof row['id'] === 'string' ? row['id'] : `article-block-${index}`,
      })
    )
    .sort(
      (left, right) => Number(left['order'] ?? 0) - Number(right['order'] ?? 0)
    );
}

function renderTableOfContents(
  toc: ReturnType<typeof deriveKbArticleTableOfContents>,
  activeItemId: string | null,
  navigateToItem: (event: ReactMouseEvent<HTMLElement>, itemId: string) => void
): ReactElement {
  return createElement(
    'aside',
    { className: styles.kbArticleAside, 'data-sl-role': 'kb-article-aside' },
    createElement(
      'nav',
      {
        className: styles.kbArticleToc,
        'aria-label': 'Article table of contents',
        'data-sl-role': 'kb-article-toc',
      },
      createElement('div', { className: styles.kbArticleTocTitle }, 'Inhalt'),
      createElement(
        'ol',
        { className: styles.kbArticleTocList },
        ...toc.map(item =>
          createElement(
            'li',
            {
              key: item.id,
              className: styles.kbArticleTocItem,
              'data-level': item.level,
            },
            createElement(
              'a',
              {
                className: styles.kbArticleTocLink,
                href: `#${item.id}`,
                onClick: event => navigateToItem(event, item.id),
                'aria-current':
                  activeItemId === item.id ? 'location' : undefined,
                'data-active': activeItemId === item.id ? 'true' : 'false',
                'data-sl-role': 'kb-article-toc-link',
              },
              item.text
            )
          )
        )
      )
    )
  );
}

function useArticleTableOfContents(
  articleRef: RefObject<HTMLElement | null>,
  toc: ReturnType<typeof deriveKbArticleTableOfContents>
): {
  activeItemId: string | null;
  navigateToItem: (event: ReactMouseEvent<HTMLElement>, itemId: string) => void;
} {
  const [activeItemId, setActiveItemId] = useState<string | null>(
    toc[0]?.id ?? null
  );

  const updateActiveItem = useCallback(() => {
    const article = articleRef.current;
    if (!article || toc.length === 0) {
      setActiveItemId(null);
      return;
    }
    const scrollContainer = findNearestScrollContainer(article);
    const viewportTop = scrollContainer
      ? scrollContainer.getBoundingClientRect().top
      : 0;
    const viewportHeight = scrollContainer?.clientHeight ?? window.innerHeight;
    const activationLine = viewportTop + Math.min(180, viewportHeight * 0.25);
    let nextActiveId = toc[0]?.id ?? null;
    const atScrollEnd = scrollContainer
      ? scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 1
      : window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 1;
    if (atScrollEnd) {
      setActiveItemId(current => {
        const lastItemId = toc.at(-1)?.id ?? null;
        return current === lastItemId ? current : lastItemId;
      });
      return;
    }
    for (const item of toc) {
      const target = findArticleSection(article, item.id);
      if (!target) continue;
      if (target.getBoundingClientRect().top <= activationLine) {
        nextActiveId = item.id;
        continue;
      }
      break;
    }
    setActiveItemId(current =>
      current === nextActiveId ? current : nextActiveId
    );
  }, [articleRef, toc]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const article = articleRef.current;
    if (!article || toc.length === 0) return;
    const scrollContainer = findNearestScrollContainer(article);
    const scrollTarget: HTMLElement | Window = scrollContainer ?? window;
    let frame = 0;
    const scheduleUpdate = () => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateActiveItem();
      });
    };
    updateActiveItem();
    scrollTarget.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      scrollTarget.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [articleRef, toc, updateActiveItem]);

  const navigateToItem = useCallback(
    (event: ReactMouseEvent<HTMLElement>, itemId: string) => {
      const article = articleRef.current;
      const target = article ? findArticleSection(article, itemId) : null;
      if (!target) return;
      event.preventDefault();
      setActiveItemId(itemId);
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
    },
    [articleRef]
  );

  return { activeItemId, navigateToItem };
}

function findArticleSection(
  article: HTMLElement,
  itemId: string
): HTMLElement | null {
  return (
    [...article.querySelectorAll<HTMLElement>('[id]')].find(
      element => element.id === itemId
    ) ?? null
  );
}

function findNearestScrollContainer(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;
  while (current) {
    const overflowY = window.getComputedStyle(current).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function renderKbArticleBlock(
  block: KbArticleBlock,
  workspaceId?: string,
  runtimeContext?: ViewRuntimeContext
): ReactElement {
  switch (block.type) {
    case 'hero':
      return createElement(
        'section',
        {
          key: block.id,
          id: block.id,
          className: styles.kbArticleBlockHero,
          'data-sl-role': 'kb-article-block-hero',
        },
        block.eyebrow
          ? createElement(
              'p',
              { className: styles.kbArticleBlockHeroEyebrow },
              block.eyebrow
            )
          : null,
        createElement(
          'h2',
          { className: styles.kbArticleBlockHeroTitle },
          block.heading
        ),
        renderMarkdownBody(block.bodyMarkdown, styles.kbArticleBlockHeroText),
        block.image
          ? createElement(ResponsiveImage, {
              image: block.image,
              role: 'article',
              alt: block.alt ?? block.heading,
              workspaceId,
              allowWorkspaceBlobFallback: Boolean(workspaceId),
              className: styles.kbArticleFigureImage,
              style: resolveImageStyle(block),
            })
          : null,
        block.ctaLabel && block.ctaHref
          ? createElement(
              'a',
              {
                className: styles.kbArticleCtaLink,
                href: resolveCustomAppRuntimeHref(
                  block.ctaHref,
                  runtimeContext
                ),
                'data-sl-role': 'kb-article-cta-link',
              },
              block.ctaLabel
            )
          : null
      );
    case 'prose':
      return createElement(
        'section',
        {
          key: block.id,
          id: block.id,
          'data-sl-role': 'kb-article-block-prose',
        },
        block.heading
          ? createElement(
              block.headingLevel,
              { className: styles.kbArticleHeading },
              block.heading
            )
          : null,
        renderMarkdownBody(block.bodyMarkdown, styles.kbArticleParagraph)
      );
    case 'heading':
      return createElement(
        block.level,
        {
          key: block.id,
          id: block.id,
          className: styles.kbArticleHeading,
          'data-level': block.level,
          'data-sl-role': 'kb-article-heading',
        },
        block.text
      );
    case 'paragraph':
      return createElement(
        'p',
        {
          key: block.id,
          className: styles.kbArticleParagraph,
          'data-sl-role': 'kb-article-paragraph',
        },
        block.text
      );
    case 'callout':
      return createElement(
        'aside',
        {
          key: block.id,
          className: styles.kbArticleCallout,
          'data-tone': block.tone,
          'data-sl-role': 'kb-article-callout',
        },
        block.title
          ? createElement(
              'strong',
              { className: styles.kbArticleCalloutTitle },
              block.title
            )
          : null,
        createElement(
          'p',
          { className: styles.kbArticleCalloutText },
          block.text
        )
      );
    case 'image':
      return createElement(
        'figure',
        {
          key: block.id,
          className: styles.kbArticleFigure,
          style: resolveImageFrameStyle(block),
          'data-display-width': block.displayWidth,
          'data-sl-role': 'kb-article-figure',
        },
        block.image
          ? createElement(ResponsiveImage, {
              image: block.image,
              role: 'article',
              alt: block.alt,
              workspaceId,
              allowWorkspaceBlobFallback: Boolean(workspaceId),
              className: styles.kbArticleFigureImage,
              style: resolveImageStyle(block),
            })
          : createElement('div', {
              className: styles.kbArticleFigurePlaceholder,
              'aria-label': block.alt,
            }),
        block.caption
          ? createElement(
              'figcaption',
              { className: styles.kbArticleFigureCaption },
              block.caption
            )
          : null
      );
    case 'imageText':
      return createElement(
        'section',
        {
          key: block.id,
          id: block.id,
          className: styles.kbArticleImageText,
          'data-image-position': block.imagePosition,
          'data-display-width': block.displayWidth,
          'data-sl-role': 'kb-article-image-text',
          style: resolveImageFrameStyle(block),
        },
        block.image
          ? createElement(ResponsiveImage, {
              image: block.image,
              role: 'article',
              alt: block.alt,
              workspaceId,
              allowWorkspaceBlobFallback: Boolean(workspaceId),
              className: styles.kbArticleFigureImage,
              style: resolveImageStyle(block),
            })
          : createElement('div', {
              className: styles.kbArticleFigurePlaceholder,
              'aria-label': block.alt,
            }),
        createElement(
          'div',
          { className: styles.kbArticleImageTextContent },
          block.heading
            ? createElement(
                block.headingLevel,
                { className: styles.kbArticleHeading },
                block.heading
              )
            : null,
          renderMarkdownBody(block.bodyMarkdown, styles.kbArticleParagraph),
          block.caption
            ? createElement(
                'p',
                { className: styles.kbArticleFigureCaption },
                block.caption
              )
            : null
        )
      );
    case 'steps':
      return createElement(
        'section',
        {
          key: block.id,
          className: styles.kbArticleSteps,
          'data-sl-role': 'kb-article-steps',
        },
        block.title
          ? createElement(
              'h3',
              { className: styles.kbArticleStepsTitle },
              block.title
            )
          : null,
        createElement(
          'ol',
          { className: styles.kbArticleStepsList },
          ...block.items.map((item, index) =>
            createElement(
              'li',
              {
                key: `${block.id}-${index}`,
                className: styles.kbArticleStepItem,
              },
              item
            )
          )
        )
      );
    case 'quote':
      return createElement(
        'blockquote',
        {
          key: block.id,
          className: styles.kbArticleQuote,
          'data-sl-role': 'kb-article-quote',
        },
        createElement(
          'p',
          { className: styles.kbArticleQuoteText },
          block.text
        ),
        block.cite
          ? createElement(
              'cite',
              { className: styles.kbArticleQuoteCite },
              block.cite
            )
          : null
      );
    case 'cards':
      return createElement(
        'section',
        {
          key: block.id,
          className: styles.kbArticleCards,
          'data-sl-role': 'kb-article-cards',
        },
        block.title
          ? createElement(
              'h3',
              { className: styles.kbArticleCardsTitle },
              block.title
            )
          : null,
        createElement(
          'div',
          { className: styles.kbArticleCardGrid },
          ...block.items.map((item, index) =>
            createElement(
              item.href ? 'a' : 'div',
              {
                key: `${block.id}-${index}`,
                className: styles.kbArticleCard,
                href: item.href
                  ? resolveCustomAppRuntimeHref(item.href, runtimeContext)
                  : undefined,
              },
              createElement('strong', null, item.title),
              createElement(
                'span',
                { className: styles.kbArticleCardText },
                item.text
              )
            )
          )
        )
      );
    case 'cta':
      return createElement(
        'section',
        {
          key: block.id,
          className: styles.kbArticleCta,
          'data-sl-role': 'kb-article-cta',
        },
        block.title
          ? createElement(
              'h3',
              { className: styles.kbArticleCtaTitle },
              block.title
            )
          : null,
        block.text
          ? createElement(
              'p',
              { className: styles.kbArticleCtaText },
              block.text
            )
          : null,
        createElement(
          'a',
          {
            className: styles.kbArticleCtaLink,
            href: resolveCustomAppRuntimeHref(block.href, runtimeContext),
            'data-sl-role': 'kb-article-cta-link',
          },
          block.label
        )
      );
    case 'table':
      return createElement(
        'section',
        {
          key: block.id,
          id: block.id,
          className: styles.kbArticleCards,
          'data-sl-role': 'kb-article-table-block',
        },
        block.heading
          ? createElement(
              'h2',
              { className: styles.kbArticleCardsTitle },
              block.heading
            )
          : null,
        createElement(
          'div',
          {
            className: styles.kbArticleTableScroll,
            tabIndex: 0,
            role: 'region',
            'aria-label': block.heading ?? 'Article table',
            'data-sl-role': 'kb-article-table-scroll',
          },
          createElement(
            'table',
            { className: styles.kbArticleTable },
            createElement(
              'thead',
              null,
              createElement(
                'tr',
                null,
                ...block.columns.map(column =>
                  createElement('th', { key: column }, column)
                )
              )
            ),
            createElement(
              'tbody',
              null,
              ...block.rows.map((row, rowIndex) =>
                createElement(
                  'tr',
                  { key: `${block.id}-${rowIndex}` },
                  ...row.map((cell, cellIndex) =>
                    createElement('td', { key: cellIndex }, cell)
                  )
                )
              )
            )
          )
        )
      );
    case 'relatedArticles':
      return renderRelatedArticlesBlock(block, runtimeContext);
    case 'code':
      return createElement(
        'figure',
        {
          key: block.id,
          className: styles.kbArticleCodeBlock,
          'data-sl-role': 'kb-article-code',
        },
        block.language
          ? createElement(
              'figcaption',
              { className: styles.kbArticleCodeLanguage },
              block.language
            )
          : null,
        createElement(
          'pre',
          { className: styles.kbArticleCode },
          createElement('code', null, block.code)
        )
      );
    case 'embed_video':
      return createElement(KbExternalVideoBlock, {
        key: block.id,
        block,
        workspaceId,
      });
  }
}

function KbExternalVideoBlock({
  block,
  workspaceId,
}: {
  block: Extract<KbArticleBlock, { type: 'embed_video' }>;
  workspaceId?: string;
}): ReactElement {
  const [activated, setActivated] = useState(!block.consentRequired);
  const watchUrl = externalVideoWatchUrl(block);

  return createElement(
    'figure',
    {
      className: styles.kbArticleVideo,
      'data-sl-role': 'kb-article-video',
      'data-provider': block.provider,
    },
    createElement('h2', { className: styles.kbArticleVideoTitle }, block.title),
    activated
      ? createElement('iframe', {
          src: externalVideoEmbedUrl(block),
          title: block.title,
          loading: 'lazy',
          allow:
            'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
          allowFullScreen: true,
          referrerPolicy: 'strict-origin-when-cross-origin',
          className: styles.kbArticleVideoFrame,
        })
      : createElement(
          'div',
          {
            className: styles.kbArticleVideoConsent,
          },
          block.poster
            ? createElement(ResponsiveImage, {
                image: block.poster,
                role: 'article',
                alt: block.posterAlt ?? '',
                workspaceId,
                allowWorkspaceBlobFallback: Boolean(workspaceId),
                className: styles.kbArticleVideoPoster,
              })
            : null,
          createElement(
            'button',
            {
              type: 'button',
              onClick: () => setActivated(true),
              'aria-label': `${block.title} laden`,
              className: styles.kbArticleVideoConsentButton,
            },
            'Externes Video laden'
          )
        ),
    block.caption
      ? createElement(
          'figcaption',
          { className: styles.kbArticleFigureCaption },
          block.caption
        )
      : null,
    createElement(
      'noscript',
      null,
      createElement(
        'a',
        { href: watchUrl, rel: 'noopener noreferrer' },
        `${block.title} bei ${block.provider} ansehen`
      )
    )
  );
}

function renderMarkdownBody(
  value: string | null | undefined,
  className: string
): ReactElement | null {
  const markdown = normalizeKbArticleMarkdown(value);
  if (!markdown) return null;
  return createElement('div', {
    className: `${markdownStyles.markdownContent} ${className}`,
    dangerouslySetInnerHTML: {
      __html: renderSanitizedMarkdown(markdown),
    },
  });
}

function normalizeKbArticleMarkdown(
  value: string | null | undefined
): string | null {
  const normalized = String(value ?? '')
    .replace(/^#(\s+)/gm, '##$1')
    .trim();
  return normalized || null;
}

function renderRelatedArticlesBlock(
  block: Extract<KbArticleBlock, { type: 'relatedArticles' }>,
  runtimeContext?: ViewRuntimeContext
): ReactElement {
  const currentRowId =
    typeof runtimeContext?.row?.id === 'string' ? runtimeContext.row.id : null;
  const rows = readBoundRowsFromRuntimeSource(
    runtimeContext?.sources?.['content']
  ).filter(row => {
    const rowId = readBoundRowId(row);
    return (
      rowId &&
      rowId !== currentRowId &&
      (block.articleRowIds.length === 0 || block.articleRowIds.includes(rowId))
    );
  });
  const related = block.articleRowIds.length > 0 ? rows : rows.slice(0, 3);
  return createElement(
    'section',
    {
      key: block.id,
      id: block.id,
      className: styles.kbArticleCards,
      'data-sl-role': 'kb-article-related',
    },
    createElement(
      'h2',
      { className: styles.kbArticleCardsTitle },
      block.heading ?? 'Weitere Artikel'
    ),
    createElement(
      'div',
      { className: styles.kbArticleCardGrid },
      ...related.map(row => {
        const title = readString(row, ['title', 'name']) ?? 'Artikel';
        const slug = readString(row, ['slug']);
        return createElement(
          slug ? 'a' : 'div',
          {
            key: readBoundRowId(row) ?? title,
            className: styles.kbArticleCard,
            href: slug
              ? resolveRelatedArticleHref(slug, runtimeContext)
              : undefined,
          },
          createElement('strong', null, title),
          createElement(
            'span',
            { className: styles.kbArticleCardText },
            readString(row, ['summary', 'description']) ?? ''
          )
        );
      })
    )
  );
}

function resolveRelatedArticleHref(
  slug: string,
  runtimeContext?: ViewRuntimeContext
): string {
  const encodedSlug = encodeURIComponent(slug);
  const pagePath = readRuntimeString(runtimeContext?.page, 'path');
  const currentSlug = runtimeContext?.routeParams?.['slug'];
  const currentPageHref = resolveArticleHrefFromPath(
    pagePath,
    encodedSlug,
    currentSlug
  );
  if (currentPageHref) {
    return resolveCustomAppRuntimeHref(currentPageHref, runtimeContext);
  }

  const configuredArticlePath = readConfiguredKbArticlePath(runtimeContext);
  const configuredHref = resolveArticleHrefFromPath(
    configuredArticlePath,
    encodedSlug,
    currentSlug
  );
  return resolveCustomAppRuntimeHref(
    configuredHref ?? `/kb/${encodedSlug}`,
    runtimeContext
  );
}

function resolveArticleHrefFromPath(
  path: string | null,
  encodedSlug: string,
  currentSlug?: string
): string | null {
  if (!path) return null;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized.includes(':slug')) {
    return normalized.replaceAll(':slug', encodedSlug);
  }
  const encodedCurrentSlug = currentSlug ? encodeURIComponent(currentSlug) : '';
  if (encodedCurrentSlug && normalized.endsWith(`/${encodedCurrentSlug}`)) {
    return `${normalized.slice(0, -encodedCurrentSlug.length)}${encodedSlug}`;
  }
  return null;
}

function readConfiguredKbArticlePath(
  runtimeContext?: ViewRuntimeContext
): string | null {
  const customAppConfig = readRuntimeRecord(
    runtimeContext?.customApp,
    'config'
  );
  const pages = Array.isArray(customAppConfig?.['pages'])
    ? customAppConfig['pages']
    : [];
  for (const page of pages) {
    if (!page || typeof page !== 'object') continue;
    const record = page as Record<string, unknown>;
    if (record['templateKey'] !== 'knowledgebase-article') continue;
    const path = record['path'];
    return typeof path === 'string' && path.trim() ? path.trim() : null;
  }
  return null;
}

function readRuntimeString(
  record: Record<string, unknown> | null | undefined,
  key: string
): string | null {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readRuntimeRecord(
  record: Record<string, unknown> | null | undefined,
  key: string
): Record<string, unknown> | null {
  const value = record?.[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function useReadingProgress(): number {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const update = () => {
      const root = document.documentElement;
      const max = root.scrollHeight - window.innerHeight;
      setProgress(
        max <= 0 ? 1 : Math.min(1, Math.max(0, window.scrollY / max))
      );
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return progress;
}

function readCell(row: BoundDataRow, keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return undefined;
}

function readString(row: BoundDataRow, keys: readonly string[]): string | null {
  const value = readCell(row, keys);
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function readBoundRowId(row: BoundDataRow): string | null {
  const value = row['id'] ?? row['_id'] ?? row['rowId'];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readBoundRowsFromRuntimeSource(source: unknown): BoundDataRow[] {
  if (!source || typeof source !== 'object') return [];
  const record = source as {
    columns?: Array<{ id?: unknown; name?: unknown }>;
    rows?: Array<{ id?: unknown; cells?: Record<string, unknown> }>;
  };
  const columns = record.columns ?? [];
  return (record.rows ?? []).map(row => {
    const mapped: BoundDataRow = {};
    if (typeof row.id === 'string') {
      mapped.id = row.id;
      mapped.rowId = row.id;
    }
    for (const [key, value] of Object.entries(row.cells ?? {})) {
      mapped[key] = value;
    }
    for (const column of columns) {
      if (typeof column.id !== 'string' || typeof column.name !== 'string') {
        continue;
      }
      if (
        row.cells &&
        Object.prototype.hasOwnProperty.call(row.cells, column.id)
      ) {
        mapped[column.name] = row.cells[column.id];
      }
    }
    return mapped;
  });
}

function resolveImageStyle(
  block: Extract<KbArticleBlock, { type: 'hero' | 'image' | 'imageText' }>
): CSSProperties {
  return {
    aspectRatio: '16 / 9',
    objectFit: block.cropMode,
    objectPosition: `${block.focalX}% ${block.focalY}%`,
  };
}

function resolveImageFrameStyle(
  block: Extract<KbArticleBlock, { type: 'image' | 'imageText' }>
): CSSProperties {
  if (block.displayWidth === 'full') return { maxWidth: '100%' };
  if (block.displayWidth === 'inline') {
    return {
      maxWidth: 'min(520px, 100%)',
      marginLeft: 'auto',
      marginRight: 'auto',
    };
  }
  return {
    maxWidth: 'min(860px, 100%)',
    marginLeft: 'auto',
    marginRight: 'auto',
  };
}

function resolveAuthorName(
  row: BoundDataRow,
  runtimeContext: ViewRuntimeContext | undefined
): string | null {
  const authorValue = readCell(row, ['author', 'authorName', 'owner']);
  const authorRowId = Array.isArray(authorValue)
    ? authorValue.find((item): item is string => typeof item === 'string')
    : typeof authorValue === 'string'
      ? authorValue
      : null;
  if (authorRowId) {
    const authorName = readRelatedSourceRowLabel(
      runtimeContext?.sources?.['users'],
      authorRowId
    );
    if (authorName) return authorName;
  }
  return readString(row, ['authorName', 'owner']);
}

function readRelatedSourceRowLabel(
  source: unknown,
  rowId: string
): string | null {
  if (!source || typeof source !== 'object') return null;
  const record = source as {
    columns?: Array<{ id?: unknown; name?: unknown }>;
    rows?: Array<{ id?: unknown; cells?: Record<string, unknown> }>;
  };
  const row = record.rows?.find(candidate => candidate.id === rowId);
  if (!row) return null;
  const titleColumn = record.columns?.find(column => column.name === 'title');
  const slugColumn = record.columns?.find(column => column.name === 'slug');
  const label =
    readSourceCell(row, titleColumn?.id) ??
    readSourceCell(row, slugColumn?.id) ??
    row.id;
  return typeof label === 'string' && label.trim() ? label.trim() : null;
}

function readSourceCell(
  row: { cells?: Record<string, unknown> },
  columnId: unknown
): unknown {
  if (typeof columnId !== 'string') return undefined;
  return row.cells?.[columnId];
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('de', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(parsed));
}
